import { Formik } from 'formik'
import { type Ref, useEffect } from 'react'
import MaskedInput from 'react-text-mask'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { Select, TextField, ErrorMessage } from '@loadsmart/loadsmart-ui'
import { type Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import { Button, Layout, Text } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'

import { PHONE_WITH_COUNTRY_MASK } from 'legacy/constants/masks'

import { type Employee } from '../domain/Employee'
import { useCreateAndAssignDriver } from './hooks/useCreateAndAssignDriver'

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  phoneNumber: Yup.string().required('Phone number is required'),
  roles: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string(),
        label: Yup.string(),
      })
    )
    .min(1, 'At least 1 role is required'),
})

const initialValues = {
  name: '',
  phoneNumber: '',
  roles: [],
}

export const employeeRoles = [
  {
    value: 'driver',
    label: 'Driver',
  },
  {
    value: 'dispatcher',
    label: 'Dispatcher',
  },
]

export type DriverFormProps = {
  carrierUUID: string
  shipmentUUID: string
  onDriverSaved: (driver: Employee) => void
}

export default function DriverForm({ carrierUUID, shipmentUUID, onDriverSaved }: DriverFormProps) {
  const { createAndAssign, requestStatus, driver } = useCreateAndAssignDriver()

  useEffect(() => {
    if (requestStatus === RequestStatus.Done && driver) {
      toast.success('Driver saved with success')
      onDriverSaved(driver)
    }
  }, [driver, onDriverSaved, requestStatus])

  useEffect(() => {
    if (requestStatus === RequestStatus.Error) {
      toast.error('Failed to create new driver')
    }
  }, [requestStatus])

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, formikHelpers) => {
        await createAndAssign({ carrierUUID, shipmentUUID, newEmployee: values })
        formikHelpers.resetForm()
      }}
      enableReinitialize
      validateOnMount
    >
      {({
        handleChange,
        handleBlur,
        setFieldTouched,
        setFieldValue,
        submitForm,
        values,
        isValid,
        errors,
        touched,
        isSubmitting,
      }) => {
        const disableSubmit = !isValid || isSubmitting

        return (
          <form>
            <Layout.Stack align="flex-start">
              <Layout.Group>
                <Layout.Stack gap="spacing-1">
                  <Text variant="body-md" color="color-text-tertiary">
                    <label htmlFor="name">Driver's name</label>
                  </Text>
                  <TextField
                    id="name"
                    name="name"
                    value={values.name}
                    placeholder="e. g. Jack Smith"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {touched.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                </Layout.Stack>
                <Layout.Stack gap="spacing-1">
                  <Text variant="body-md" color="color-text-tertiary">
                    <label htmlFor="phoneNumber">Driver's phone</label>
                  </Text>
                  <MaskedInput
                    id="phoneNumber"
                    name="phoneNumber"
                    value={values.phoneNumber}
                    mask={PHONE_WITH_COUNTRY_MASK}
                    placeholder="e. g. +1 123-456-789"
                    aria-required="true"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    render={(ref, props) => (
                      <TextField ref={ref as Ref<HTMLInputElement>} {...props} type="tel" />
                    )}
                  />
                  {touched.phoneNumber && <ErrorMessage>{errors.phoneNumber}</ErrorMessage>}
                </Layout.Stack>
                <Layout.Stack gap="spacing-1">
                  <Text variant="body-md" color="color-text-tertiary">
                    <label htmlFor="roles">Role</label>
                  </Text>
                  <Select
                    multiple
                    placeholder="Select roles"
                    id="roles"
                    name="roles"
                    options={employeeRoles}
                    onChange={event => {
                      handleBlur(event)
                      if (event.target.value === null) {
                        setFieldValue('roles', [])
                        return
                      }

                      handleChange(event)
                    }}
                    onBlur={() => setFieldTouched('roles')}
                    value={values?.roles as Selectable[]}
                  />
                  {touched.roles && <ErrorMessage>{errors.roles}</ErrorMessage>}
                </Layout.Stack>
              </Layout.Group>
              <Button type="button" onClick={submitForm} variant="primary" disabled={disableSubmit}>
                Assign driver and save
              </Button>
            </Layout.Stack>
          </form>
        )
      }}
    </Formik>
  )
}
